import React, { useState, useEffect } from 'react';
import { Calendar, Gavel, AlertTriangle, CheckSquare, Clock, CalendarDays } from 'lucide-react';
import caseEventsService from '../../services/caseEventsService';

// A case-scoped, chronological timeline aggregating everything already recorded
// against one case (calendar, appointments, task due-dates, litigation events,
// deadlines). Read-only. Firm-local times come straight from the server
// (`local_label`) so they are correct regardless of the viewer's own timezone.

const KIND = {
  appointment:      { icon: Calendar,      label: 'Appointment', cls: 'text-green-300  bg-green-500/15  border-green-500/30' },
  court_date:       { icon: Gavel,         label: 'Court date',  cls: 'text-amber-300  bg-amber-500/15  border-amber-500/30' },
  hearing:          { icon: Gavel,         label: 'Hearing',     cls: 'text-purple-300 bg-purple-500/15 border-purple-500/30' },
  deadline:         { icon: AlertTriangle, label: 'Deadline',    cls: 'text-red-300    bg-red-500/15    border-red-500/30' },
  task_due:         { icon: CheckSquare,   label: 'Task due',    cls: 'text-slate-300  bg-slate-500/15  border-slate-500/30' },
  litigation_event: { icon: Clock,         label: 'Litigation',  cls: 'text-cyan-300   bg-cyan-500/15   border-cyan-500/30' },
  event:            { icon: Calendar,      label: 'Event',       cls: 'text-slate-300  bg-slate-500/15  border-slate-500/30' },
};

const Row = ({ ev, past }) => {
  const meta = KIND[ev.kind] || KIND.event;
  const Icon = meta.icon;
  return (
    <li className={`flex items-start gap-3 py-2.5 ${past ? 'opacity-60' : ''}`}>
      <span className={`mt-0.5 shrink-0 rounded-md border p-1.5 ${meta.cls}`}>
        <Icon size={15} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-virgo-navy-50 break-words">{ev.title}</span>
          <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium border ${meta.cls}`}>{meta.label}</span>
        </div>
        <div className="text-sm text-virgo-navy-300">
          {ev.local_label || 'No date set'}
          {ev.location ? <span className="text-virgo-navy-400"> · {ev.location}</span> : null}
        </div>
      </div>
    </li>
  );
};

const CaseTimeline = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    caseEventsService
      .getTimeline(caseId)
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch(() => { if (alive) setError('Could not load the case timeline.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [caseId]);

  const events = data?.events || [];
  const upcoming = events.filter((e) => !e.is_past);
  const past = events.filter((e) => e.is_past).reverse(); // most-recent first

  return (
    <div>
      <div className="flex items-center gap-2 text-virgo-gold-400 font-semibold">
        <CalendarDays size={16} /> Case Timeline
        {data ? <span className="text-sm font-normal text-virgo-navy-400">({data.counts.total})</span> : null}
      </div>

      {loading && <div className="mt-3 text-sm text-virgo-navy-400">Loading…</div>}
      {error && <div className="mt-3 text-sm text-red-300">{error}</div>}

      {!loading && !error && events.length === 0 && (
        <div className="mt-3 rounded-lg border border-virgo-navy-600 bg-virgo-navy-800/40 px-4 py-6 text-center text-sm text-virgo-navy-300">
          No scheduled events for this case yet.
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <div className="mt-3 space-y-4">
          {upcoming.length > 0 && (
            <section>
              <h4 className="text-xs uppercase tracking-wide text-virgo-navy-400 mb-1">Upcoming</h4>
              <ul className="divide-y divide-virgo-navy-700">
                {upcoming.map((e) => <Row key={e.id} ev={e} past={false} />)}
              </ul>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h4 className="text-xs uppercase tracking-wide text-virgo-navy-500 mb-1">Past</h4>
              <ul className="divide-y divide-virgo-navy-800">
                {past.map((e) => <Row key={e.id} ev={e} past />)}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseTimeline;
